import {
  SignatureAuthMethod,
  type SignatureManifestEntry,
} from '@qualicloud/contracts';
import { IconShieldCheck } from '@/design-system';
import { cn } from '@/design-system/utils';
import { formatRegulatedDateTime, truncateHash } from '@/lib/format/datetime';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MANIFESTO DE ASSINATURAS ELETRÔNICAS — 21 CFR §11.50
 * Formato do plano §5.4.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * §11.50(a) — cada assinatura exibe: nome impresso do signatário, data e hora
 *     do ato, e o SIGNIFICADO da assinatura.
 *
 * §11.50(b) — a informação do §11.50(a) fica sujeita AOS MESMOS CONTROLES do
 *     registro assinado e aparece em qualquer forma legível do registro,
 *     inclusive impressa. Por isso este componente é usado em três lugares com
 *     o MESMO shape de dado (`signatureManifestEntrySchema[]`):
 *       1. detalhe do registro na tela;
 *       2. versão de impressão (`variant="print"`, sem cromo de aplicação);
 *       3. PDF/A gerado pelo serviço MEDIA, a partir do mesmo array.
 *
 * `meaningText` é CONGELADO no ato e nunca recalculado a partir de
 * `meaningCode` — se a organização renomear o significado no futuro,
 * assinaturas antigas continuam mostrando o texto vigente na data. Recalcular
 * mudaria a exibição retroativamente, o que é falha direta de §11.50(b).
 *
 * A data usa o `displayTimeZone` que veio JUNTO da assinatura, não o fuso atual
 * da organização: se a organização mudar de fuso, o carimbo histórico continua
 * legível como foi assinado.
 */

export interface SignatureManifestProps {
  readonly entries: readonly SignatureManifestEntry[];
  /**
   * `screen` — bloco na aba do registro.
   * `print`  — versão de impressão: bordas sólidas, sem cor de fundo, sem
   *            elementos interativos. Sempre visível na mídia de impressão.
   */
  readonly variant?: 'screen' | 'print';
  /** Rótulo do registro, exibido quando a entrada não o traz. */
  readonly recordLabelFallback?: string;
  readonly className?: string;
  /** Nível do heading, para manter a hierarquia da página. */
  readonly headingLevel?: 2 | 3;
}

const AUTH_METHOD_LABEL: Record<string, string> = {
  [SignatureAuthMethod.PASSWORD]: 'usuário e senha',
  [SignatureAuthMethod.PASSWORD_MFA]: 'usuário, senha e segundo fator',
};

export function SignatureManifest({
  entries,
  variant = 'screen',
  recordLabelFallback,
  className,
  headingLevel = 3,
}: SignatureManifestProps) {
  const Heading = `h${headingLevel}` as const;
  const isPrint = variant === 'print';

  return (
    <section
      aria-labelledby="signature-manifest-heading"
      className={cn(
        'qc-signature-manifest rounded-lg border',
        isPrint ? 'border-black bg-white text-black' : 'border-border bg-surface',
        className,
      )}
    >
      <header
        className={cn(
          'flex items-center gap-2 border-b px-4 py-3',
          isPrint ? 'border-black' : 'border-border-subtle bg-surface-sunken',
        )}
      >
        {!isPrint ? (
          <IconShieldCheck size={18} aria-hidden="true" className="text-brand" />
        ) : null}
        <Heading
          id="signature-manifest-heading"
          className="text-sm font-bold uppercase tracking-wide"
        >
          Assinaturas eletrônicas
        </Heading>
      </header>

      {entries.length === 0 ? (
        <p
          className={cn(
            'px-4 py-6 text-center text-sm',
            isPrint ? 'text-black' : 'text-content-subtle',
          )}
        >
          Este registro ainda não possui assinaturas eletrônicas.
        </p>
      ) : (
        <ol className="m-0 list-none p-0">
          {entries.map((entry, index) => (
            <li
              key={entry.signatureId}
              className={cn(
                'px-4 py-3.5',
                index > 0 && (isPrint ? 'border-t border-black' : 'border-t border-border-subtle'),
              )}
            >
              {/*
                Layout do plano §5.4:
                  Aprovado por    Maria Silva — Gerente da Qualidade
                                  14/03/2026 09:42:17 (UTC-03)
                                  Documento: POP-042 v2.0
                                  Hash: a3f8…9c21
              */}
              <div className="grid gap-x-4 gap-y-1 sm:grid-cols-[10rem_1fr]">
                <p
                  className={cn(
                    'text-sm font-bold',
                    isPrint ? 'text-black' : 'text-content',
                  )}
                >
                  {/* Texto CONGELADO no ato — nunca derivado de meaningCode. */}
                  {entry.meaningText}
                </p>

                <div className="min-w-0">
                  <p className={cn('text-sm font-semibold', isPrint ? 'text-black' : 'text-content')}>
                    {entry.signerName}
                    <span className={cn('font-normal', isPrint ? '' : 'text-content-muted')}>
                      {' — '}
                      {entry.signerRole}
                    </span>
                  </p>

                  <p
                    className={cn(
                      'text-sm qc-tabular',
                      isPrint ? 'text-black' : 'text-content-muted',
                    )}
                  >
                    {/* Fuso SEMPRE visível: carimbo sem fuso é ambíguo. */}
                    {formatRegulatedDateTime(entry.signedAt, entry.displayTimeZone)}
                  </p>

                  <p className={cn('text-sm', isPrint ? 'text-black' : 'text-content-muted')}>
                    {entry.recordLabel || recordLabelFallback || 'Registro'}
                  </p>

                  <p
                    className={cn(
                      'text-xs font-mono',
                      isPrint ? 'text-black' : 'text-content-subtle',
                    )}
                  >
                    Hash: {truncateHash(entry.recordHash)}
                    {/* O hash completo fica disponível para conferência, mas
                        truncado na leitura: 64 caracteres poluem o bloco. */}
                    <span className="qc-sr-only"> (SHA-256 completo: {entry.recordHash})</span>
                  </p>

                  <p className={cn('text-xs', isPrint ? 'text-black' : 'text-content-subtle')}>
                    Autenticação:{' '}
                    {AUTH_METHOD_LABEL[entry.authMethod] ?? entry.authMethod.toLowerCase()}
                  </p>

                  {entry.reason ? (
                    <p className={cn('mt-1 text-sm', isPrint ? 'text-black' : 'text-content')}>
                      <span className="font-semibold">Justificativa: </span>
                      {entry.reason}
                    </p>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* Rodapé exigido pelo plano §5.4 — sai também na impressão e no PDF/A. */}
      <footer
        className={cn(
          'border-t px-4 py-3 text-xs',
          isPrint ? 'border-black text-black' : 'border-border-subtle bg-surface-sunken text-content-muted',
        )}
      >
        Assinaturas eletrônicas aplicadas conforme 21 CFR Part 11 e legalmente vinculantes.
      </footer>
    </section>
  );
}

/**
 * Versão de impressão do registro + manifesto.
 *
 * Fica oculta na tela (`qc-print-only`) e aparece só na mídia de impressão.
 * Existe porque §11.50(b) exige que o manifesto acompanhe QUALQUER forma
 * legível do registro — imprimir a tela sem ele produziria uma cópia
 * não conforme.
 */
export function PrintableRecord({
  title,
  subtitle,
  metadata,
  entries,
  watermark,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly metadata: ReadonlyArray<{ readonly label: string; readonly value: string }>;
  readonly entries: readonly SignatureManifestEntry[];
  /** Texto de marca d'água (`WATERMARK_TEXT` do domínio) quando aplicável. */
  readonly watermark?: string | null;
}) {
  return (
    <div className="qc-print-only">
      {watermark ? (
        <p
          className="mb-4 border-2 border-black px-3 py-2 text-center text-lg font-black uppercase tracking-widest text-black"
          // Marca d'água precisa ser LIDA, não só vista: entra no fluxo do
          // documento e é anunciada pelo leitor de tela.
          role="note"
        >
          {watermark}
        </p>
      ) : null}

      <h1 className="text-xl font-bold text-black">{title}</h1>
      {subtitle ? <p className="text-base text-black">{subtitle}</p> : null}

      <dl className="mt-4 grid grid-cols-[12rem_1fr] gap-x-4 gap-y-1 text-sm text-black">
        {metadata.map((item) => (
          <div key={item.label} className="contents">
            <dt className="font-semibold">{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>

      <SignatureManifest entries={entries} variant="print" className="mt-6" headingLevel={2} />

      <p className="mt-4 text-xs text-black">
        Documento impresso a partir do QualiCloud eQMS em{' '}
        {new Date().toLocaleString('pt-BR')}. Cópias impressas não são controladas, salvo quando
        identificadas como cópia controlada.
      </p>
    </div>
  );
}
